import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { notesApi } from '@/lib/notesApi';
import { useNotesAuth } from '@/lib/notesAuth';
import { toast } from 'sonner';
import { extractError } from '@/lib/errors';
import { Loader2, Diamond, AlertTriangle, RotateCcw, Trash2, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { TagWorkspaceShell } from './TagWorkspaceShell';
import { TagToolbar } from './TagToolbar';
import { TagJewelleryCard } from './TagJewelleryCard';
import { TagManualTagDialog } from './TagManualTagDialog';
import { tagThemeClass } from './tagTheme';
import { JewelleryBatchPrintDialog } from '@/components/jewellery/JewelleryBatchPrintDialog';
import { fileToDataUrlUnderCap } from '@/lib/imageResize';

/**
 * TagWorkspacePage — the main (and only) authenticated page for the neutral
 * tag domain. No KPI/summary tiles. Shows company jewellery (read-only) plus
 * manual tags, with restricted card info (no value / goldsmith / order refs).
 */
export default function TagWorkspacePage() {
  const { principal, logout } = useNotesAuth();
  const [params] = useSearchParams();
  const headerVariant = (params.get('header') || 'c').toLowerCase();

  const [ctx, setCtx] = useState(null);
  const [items, setItems] = useState([]);
  const [masters, setMasters] = useState({ gold_purities: [], jewellery_types: [] });
  const [initialLoading, setInitialLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState({ total: 0, total_pages: 1, page_size: 30 });

  const [batchOpen, setBatchOpen] = useState(false);
  const [manual, setManual] = useState({ open: false, mode: 'create', item: null });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [uploadingImageId, setUploadingImageId] = useState(null);

  // Context + masters load once.
  const loadMeta = useCallback(async () => {
    try {
      const [cRes, mRes] = await Promise.all([
        notesApi.get('/notes/tag-workspace/context'),
        notesApi.get('/notes/tag-workspace/masters'),
      ]);
      setCtx(cRes.data);
      setMasters(mRes.data || { gold_purities: [], jewellery_types: [] });
    } catch (e) {
      setError(extractError(e, 'Could not load the workspace.'));
      setInitialLoading(false);
    }
  }, []);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  // Debounce the search box (350ms) so each keystroke doesn't hit the server.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Fetch exactly one page (30 cards) from the server. Search + date filtering
  // happen server-side, so results span the whole dataset while only 30 heavy
  // image payloads travel per request.
  const fetchPage = useCallback(async () => {
    setError('');
    setFetching(true);
    try {
      const params = { page, page_size: 30 };
      if (debouncedSearch) params.q = debouncedSearch;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await notesApi.get('/notes/tag-workspace/jewellery', { params });
      const tp = data?.total_pages || 1;
      // If a deletion emptied the last page, step back onto a populated page.
      if ((data?.items || []).length === 0 && (data?.total || 0) > 0 && page > tp) {
        setPage(tp);
        return;
      }
      setItems(data?.items || []);
      setPageInfo({ total: data?.total || 0, total_pages: tp, page_size: data?.page_size || 30 });
    } catch (e) {
      setError(extractError(e, 'Could not load the workspace.'));
    } finally {
      setFetching(false);
      setInitialLoading(false);
    }
  }, [page, debouncedSearch, dateFrom, dateTo]);

  // Run the fetch on page/filter change. A filter change resets to page 1
  // (which itself re-triggers this effect) to avoid landing on an empty page.
  const filterSig = `${debouncedSearch}|${dateFrom}|${dateTo}`;
  const sigRef = useRef(filterSig);
  useEffect(() => {
    if (sigRef.current !== filterSig) {
      sigRef.current = filterSig;
      if (page !== 1) { setPage(1); return; }
    }
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterSig]);

  const reloadList = useCallback(() => { fetchPage(); }, [fetchPage]);

  const onReset = () => { setSearch(''); setDateFrom(''); setDateTo(''); };
  const hasFilter = !!(debouncedSearch || dateFrom || dateTo);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await notesApi.delete(`/notes/tag-workspace/manual/${deleteTarget.id}`);
      toast.success('Tag deleted.');
      setDeleteTarget(null);
      reloadList();
    } catch (e) {
      toast.error(extractError(e, 'Could not delete the tag.'));
    } finally {
      setDeleting(false);
    }
  };

  const handleReplaceImage = useCallback(async (item, file) => {
    if (!item || !file) return;
    const capKb = Number(ctx?.image_cap_kb) || 500;
    const capBytes = capKb * 1024;
    const capLabel = capKb >= 1024 ? `${Math.round(capKb / 1024)} MB` : `${capKb} KB`;
    setUploadingImageId(item.id);
    try {
      let dataUrl;
      try {
        dataUrl = await fileToDataUrlUnderCap(file, capBytes);
      } catch (err) {
        if (err && err.message === 'IMAGE_TOO_LARGE') {
          toast.error(`That image is too detailed to fit the ${capLabel} limit. Please choose a smaller image.`);
        } else {
          toast.error(err?.message || 'Could not read that image.');
        }
        return;
      }
      const { data } = await notesApi.post(`/notes/tag-workspace/jewellery/${item.id}/image`, { image_data_url: dataUrl });
      // Patch the item in place so the new photo shows immediately.
      setItems((prev) => prev.map((j) => (j.id === item.id ? { ...j, image: data?.image ?? dataUrl } : j)));
      toast.success(item.is_manual ? 'Image updated.' : 'Image updated — synced to the main portal.');
    } catch (e) {
      toast.error(extractError(e, 'Could not update the image.'));
    } finally {
      setUploadingImageId(null);
    }
  }, [ctx]);

  const toolbar = (
    <TagToolbar
      search={search} setSearch={setSearch}
      dateFrom={dateFrom} setDateFrom={setDateFrom}
      dateTo={dateTo} setDateTo={setDateTo}
      onReset={onReset} count={items.length} total={pageInfo.total}
      canCreate={!!ctx}
      onCreate={() => setManual({ open: true, mode: 'create', item: null })}
      onBatch={() => setBatchOpen(true)} batchDisabled={items.length === 0}
    />
  );

  return (
    <TagWorkspaceShell
      variant={headerVariant}
      principal={principal} companyName={ctx?.company_name} isTagAdmin={!!ctx?.is_tag_admin}
      onLogout={logout} toolbar={toolbar}
    >
      {initialLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 md:gap-5" data-testid="tagws-loading-grid">
          {Array.from({ length: 8 }).map((_, i) => (<div key={i} className="h-72 animate-pulse rounded-xl border border-border bg-secondary/50" data-testid="tagws-loading-card" />))}
        </div>
      ) : error ? (
        <div className="mx-auto max-w-md rounded-xl border border-destructive/30 bg-destructive/5 p-6 text-center" data-testid="tagws-error-state">
          <AlertTriangle className="mx-auto mb-3 text-destructive" size={28} />
          <div className="font-medium text-foreground">{error}</div>
          <button onClick={fetchPage} data-testid="tagws-error-retry-button" className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"><RotateCcw size={14} /> Retry</button>
        </div>
      ) : items.length === 0 ? (
        <div className="mx-auto max-w-md rounded-xl border border-border bg-card p-10 text-center shadow-sm" data-testid="tagws-empty-state">
          <Diamond className="mx-auto mb-3 text-[hsl(var(--ring))]" size={30} />
          <div className="font-medium text-foreground">{hasFilter ? 'No matches' : 'No jewellery yet'}</div>
          <p className="mt-1 text-sm text-muted-foreground">{hasFilter ? 'Try adjusting your search or date range.' : 'Create your first manual tag to get started.'}</p>
          {ctx && !hasFilter && <button onClick={() => setManual({ open: true, mode: 'create', item: null })} data-testid="tagws-empty-create-button" className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"><Plus size={15} /> Create manual tag</button>}
        </div>
      ) : (
        <>
          <div
            className={`grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 md:gap-5 transition-opacity duration-150 ${fetching ? 'opacity-60' : 'opacity-100'}`}
            aria-busy={fetching}
            data-testid="tagws-jewellery-grid"
          >
            {items.map((it) => (
              <TagJewelleryCard key={it.id} item={it}
                onEdit={(i) => setManual({ open: true, mode: 'edit', item: i })}
                onDelete={(i) => setDeleteTarget(i)}
                onReplaceImage={handleReplaceImage}
                uploading={uploadingImageId === it.id} />
            ))}
          </div>

          {/* Pagination — 30 cards per page. */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3 sm:justify-between" data-testid="tagws-pagination">
            <div className="text-sm text-muted-foreground" data-testid="tagws-pagination-summary">
              Showing <span className="font-medium text-foreground">{(page - 1) * pageInfo.page_size + 1}</span>–
              <span className="font-medium text-foreground">{(page - 1) * pageInfo.page_size + items.length}</span>
              {' '}of <span className="font-medium text-foreground">{pageInfo.total}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || fetching}
                data-testid="tagws-pagination-prev"
                className="inline-flex h-9 items-center gap-1 rounded-lg border border-input bg-background px-3 text-sm text-foreground transition-colors duration-150 hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronLeft size={15} /> Prev
              </button>
              <span className="px-1 text-sm text-muted-foreground" data-testid="tagws-pagination-indicator">
                Page <span className="font-medium text-foreground">{page}</span> of <span className="font-medium text-foreground">{pageInfo.total_pages}</span>
              </span>
              <button
                onClick={() => setPage((p) => Math.min(pageInfo.total_pages, p + 1))}
                disabled={page >= pageInfo.total_pages || fetching}
                data-testid="tagws-pagination-next"
                className="inline-flex h-9 items-center gap-1 rounded-lg border border-input bg-background px-3 text-sm text-foreground transition-colors duration-150 hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next <ChevronRight size={15} />
              </button>
            </div>
          </div>
        </>
      )}

      <JewelleryBatchPrintDialog
        open={batchOpen} onOpenChange={setBatchOpen} items={items}
        apiClient={notesApi} batchEndpoint="/notes/tag-workspace/tags/batch"
      />

      <TagManualTagDialog
        open={manual.open} mode={manual.mode} initial={manual.item} masters={masters}
        onClose={() => setManual((m) => ({ ...m, open: false }))} onSaved={reloadList}
      />

      {deleteTarget && (
        <div className={`tag-scope ${tagThemeClass()} fixed inset-0 z-[200] flex items-center justify-center p-4`} data-testid="tagws-delete-confirmation-dialog">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={() => !deleting && setDeleteTarget(null)} />
          <div className="relative z-10 w-full max-w-sm rounded-2xl border border-border bg-card p-5 shadow-xl">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-destructive/10 text-destructive"><Trash2 size={18} /></span>
              <div>
                <h3 className="text-base font-semibold text-foreground">Delete manual tag?</h3>
                <p className="mt-1 text-sm text-muted-foreground">This permanently removes <span className="font-mono text-foreground">{deleteTarget.jewellery_no}</span> from Tag Workspace. This cannot be undone.</p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => !deleting && setDeleteTarget(null)} data-testid="tagws-delete-cancel-button" className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-4 py-2 text-sm text-foreground hover:bg-secondary">Cancel</button>
              <button onClick={confirmDelete} disabled={deleting} data-testid="tagws-delete-confirmation-button" className="inline-flex items-center gap-1.5 rounded-lg bg-destructive px-4 py-2 text-sm font-semibold text-destructive-foreground hover:opacity-90 disabled:opacity-60 active:scale-[0.98]">{deleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} Delete</button>
            </div>
          </div>
        </div>
      )}
    </TagWorkspaceShell>
  );
}
